import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { themeOf } from './cmsTheme.ts';
import { ArtistsBlock } from './ArtistsBlock.tsx';
import { useFestivalData } from '../../lib/festivalData.ts';
import { getProgramSectionModel, getFeaturesSectionModel } from '../../application/festivalSelectors.ts';
import { getProgramArtists } from '../../domain/program.ts';
import type { ArtistsSection } from '../../lib/types.ts';

/**
 * Wraps the shared list rendering (ArtistsBlock) in the standard CMS theme/guitar shell. Renders
 * either the announced artists ("Årets navne", default) or the features/"Oplevelser" list — chosen
 * by the section's `list` option — with the images on the `imageSide` side. Always fed from
 * DataContext (program.json); the section carries no authored body. The full list is always shown
 * (the per-day filter was removed with the dayFilter section — the day is shown on each card).
 */
export function CmsArtistsSection({ theme, guitar, list = 'artists', imageSide }: ArtistsSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();
  const location = useLocation();

  const isFeatures = list === 'features';
  const heading = isFeatures ? 'Midtfynske oplevelser' : 'Årets navne';
  const side = imageSide ?? (isFeatures ? 'right' : 'left');
  const filtered = data
    ? (isFeatures ? getFeaturesSectionModel(data, 'alle').filtered : getProgramSectionModel(data, 'alle').filtered)
    : [];

  // The features list is optional content — hide the whole section once loaded data has none
  // (CMS-031), matching the old dedicated features section's behaviour.
  const hideWhenEmpty = isFeatures;

  // Auto-expand + scroll on a `#artist-<id>` hash navigated from another page (e.g. HomePage's
  // artist cards linking to /program#artist-<id>). Broadcasts through the same page-local
  // `mf:select-artist` event ArtistsBlock listens on for cross-section (timetable → artist
  // card) selection (CMS-034, moved here from the bespoke ProgramPage.tsx). Only the artists
  // list owns this deep-link; a sibling features list ignores it.
  useEffect(() => {
    if (!data || isFeatures) return;
    const m = location.hash.match(/^#artist-(.+)$/);
    if (!m || !m[1]) return;
    const id = decodeURIComponent(m[1]);
    const artist = getProgramArtists(data.program).find(a => a.id === id);
    if (!artist) return;
    window.dispatchEvent(new CustomEvent('mf:select-artist', { detail: { artistId: id } }));
  }, [data, isFeatures, location.hash]);

  if (data && hideWhenEmpty && filtered.length === 0) return null;

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <CmsSectionState
        loading={loading}
        error={!!error}
        loadingLabel={isFeatures ? 'Indlæser oplevelser…' : 'Indlæser kunstnere…'}
        bodyClass={t.body}
        errorMessage={isFeatures ? 'Oplevelserne kunne ikke indlæses.' : 'Kunstnerne kunne ikke indlæses.'}
      >
        {data && <ArtistsBlock artists={filtered} heading={heading} imageSide={side} theme={theme} />}
      </CmsSectionState>
    </CmsSectionShell>
  );
}
